import { Box } from "@mui/material";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { type FC, Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { ShiftChangeField, type ShiftFieldChange } from "@/pages/ActivityLog/GroupedActivityLog/useGroupedActivityLogQuery";
import { FONT_SIZE, INK } from "@/theme/tokens";
import { ValueChange } from "@/ui/molecules/ValueChange";
import { formatLocationLabel } from "@/utils/locationDisplay";
import { NurseTrainingStatus, ShiftType } from "@/utils/models";
import { formatCurrency } from "@/utils/utilFunctions";

type Props = {
	changes: ShiftFieldChange[];
	dataTestId: string;
};

const EMPTY_VALUE = "—";
const TIME_FORMAT = "h:mm a";
const DATED_TIME_FORMAT = "MMM d, h:mm a";
const TIME_FIELDS: ReadonlySet<ShiftChangeField> = new Set([ShiftChangeField.Start, ShiftChangeField.End, ShiftChangeField.CallInTime]);

const FIELD_LABEL_KEY: Record<ShiftChangeField, string> = {
	[ShiftChangeField.Start]: "start.time",
	[ShiftChangeField.End]: "end.time",
	[ShiftChangeField.Location]: "location",
	[ShiftChangeField.Job]: "job",
	[ShiftChangeField.Notes]: "notes",
	[ShiftChangeField.Charge]: "charge",
	[ShiftChangeField.Bonus]: "shiftHistory.field.bonus",
	[ShiftChangeField.CallInTime]: "callin.time",
	[ShiftChangeField.Removed]: "shiftHistory.field.status",
	[ShiftChangeField.RemovalReason]: "shiftHistory.field.removalReason",
	[ShiftChangeField.RemovalMessage]: "shiftHistory.field.removalMessage",
	[ShiftChangeField.TrainingStatus]: "shiftHistory.field.trainingStatus",
	[ShiftChangeField.ShiftType]: "shiftHistory.field.shiftType",
	[ShiftChangeField.Published]: "shiftHistory.field.published",
};

type Formatters = { t: TFunction; timezone: string; jobNameById: Map<string, string> };

/** A time reads as a clock time, with its date only when the change moved it to another day. */
const formatTimes = (change: ShiftFieldChange, timezone: string): [string, string] => {
	const before = change.before === null ? null : DateTime.fromISO(change.before).setZone(timezone);
	const after = change.after === null ? null : DateTime.fromISO(change.after).setZone(timezone);
	const format = before !== null && after !== null && !before.hasSame(after, "day") ? DATED_TIME_FORMAT : TIME_FORMAT;
	return [before?.toFormat(format) ?? EMPTY_VALUE, after?.toFormat(format) ?? EMPTY_VALUE];
};

const formatTrainingStatus = (value: string, t: TFunction): string => {
	switch (value) {
		case NurseTrainingStatus.Regular:
			return t("shiftHistory.value.trainingStatus.Regular");
		case NurseTrainingStatus.Trainee:
			return t("shiftHistory.value.trainingStatus.Trainee");
		case NurseTrainingStatus.Trainer:
			return t("shiftHistory.value.trainingStatus.Trainer");
		case NurseTrainingStatus.Orientation:
			return t("shiftHistory.value.trainingStatus.Orientation");
		case NurseTrainingStatus.Education:
			return t("shiftHistory.value.trainingStatus.Education");
		default:
			throw new Error(`Unknown shift training status: ${value}`);
	}
};

const formatShiftType = (value: string, t: TFunction): string => {
	switch (value) {
		case ShiftType.Day:
			return t("request.outreach.shift_type.Day");
		case ShiftType.Morning:
			return t("request.outreach.shift_type.Morning");
		case ShiftType.Evening:
			return t("request.outreach.shift_type.Evening");
		case ShiftType.Afternoon:
			return t("request.outreach.shift_type.Afternoon");
		case ShiftType.Night:
			return t("request.outreach.shift_type.Night");
		case ShiftType.FullDay:
			return t("request.outreach.shift_type.Full Day");
		case ShiftType.All:
			return t("shiftHistory.value.shiftType.All");
		// DEFAULT: stored shift types are text, so historical values outside the current UI enum display verbatim.
		default:
			return value;
	}
};

const formatValue = (field: ShiftChangeField, value: string | null, { t, jobNameById }: Formatters): string => {
	if (value === null) return EMPTY_VALUE;
	switch (field) {
		case ShiftChangeField.Location:
			return formatLocationLabel(value);
		case ShiftChangeField.Job:
			// DEFAULT: a job removed from the department since has no name left to show.
			return jobNameById.get(value) ?? EMPTY_VALUE;
		case ShiftChangeField.Charge:
			return t(value === "true" ? "yes" : "no");
		case ShiftChangeField.Bonus:
			return formatCurrency(Number(value));
		case ShiftChangeField.Removed:
			return t(value === "true" ? "shiftHistory.value.removed" : "shiftHistory.value.onSchedule");
		case ShiftChangeField.TrainingStatus:
			return formatTrainingStatus(value, t);
		case ShiftChangeField.ShiftType:
			return formatShiftType(value, t);
		case ShiftChangeField.Published:
			if (value === "true") return t("published");
			if (value === "false") return t("shiftHistory.value.unpublished");
			throw new Error(`Unknown shift publication value: ${value}`);
		default:
			// The response's date conversion turns ISO-looking text into a DateTime; String() reads it back as text.
			return String(value);
	}
};

/** What one save changed on the shift, each field as it was and as it became. */
export const ShiftFieldChanges: FC<Props> = ({ changes, dataTestId }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const jobNameById = useMemo(() => new Map((chosenDepartment.jobs ?? []).map((job) => [job.id, job.displayName])), [chosenDepartment.jobs]);
	const formatters: Formatters = { t, timezone: chosenDepartment.timezone, jobNameById };

	return (
		<Box
			component="dl"
			data-testid={dataTestId}
			sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", columnGap: 1.5, rowGap: 0.5, alignItems: "baseline", m: 0, mt: 0.75 }}
		>
			{changes.map((change) => {
				const [before, after] = TIME_FIELDS.has(change.field)
					? formatTimes(change, chosenDepartment.timezone)
					: [formatValue(change.field, change.before, formatters), formatValue(change.field, change.after, formatters)];
				return (
					<Fragment key={change.field}>
						<Box component="dt" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
							{t(FIELD_LABEL_KEY[change.field])}
						</Box>
						<Box component="dd" sx={{ m: 0, minWidth: 0 }}>
							<ValueChange
								before={before}
								after={after}
								beforeLabel={t("shiftHistory.change.before")}
								afterLabel={t("shiftHistory.change.after")}
								dataTestId={`${dataTestId}-${change.field}`}
							/>
						</Box>
					</Fragment>
				);
			})}
		</Box>
	);
};
